import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Coins, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "../lib/api";
import type { AllowanceAssignment, AllowanceDetail } from "../lib/api";

// Reusable allowance-assignment panel (M14.4). Wires the 3-layer
// assignment endpoints (Grade / Position / Contract) into a single
// component. Pick which layer + the parent id; it renders the
// assignments, supports add + override-edit + remove.

type Layer = "grade" | "position" | "contract";

export interface AllowanceAssignmentsPanelProps {
  layer: Layer;
  parentId: string;
  legalEntityId: string;
}

const API_BY_LAYER = {
  grade: {
    list: (parentId: string) => api.listGradeAllowances(parentId),
    upsert: (parentId: string, body: {
      allowance_id: string;
      amount_override: string | null;
    }) => api.upsertGradeAllowance(parentId, body),
    del: (parentId: string, allowanceId: string) =>
      api.deleteGradeAllowance(parentId, allowanceId),
    queryKey: (parentId: string) => ["grade-allowances", parentId],
    titleKey: "app.aap_title_grade",
    layerLabelKey: "app.aap_layer_grade",
  },
  position: {
    list: (parentId: string) => api.listPositionAllowances(parentId),
    upsert: (parentId: string, body: {
      allowance_id: string;
      amount_override: string | null;
    }) => api.upsertPositionAllowance(parentId, body),
    del: (parentId: string, allowanceId: string) =>
      api.deletePositionAllowance(parentId, allowanceId),
    queryKey: (parentId: string) => ["position-allowances", parentId],
    titleKey: "app.aap_title_position",
    layerLabelKey: "app.aap_layer_position",
  },
  contract: {
    list: (parentId: string) => api.listContractAllowances(parentId),
    upsert: (parentId: string, body: {
      allowance_id: string;
      amount_override: string | null;
    }) => api.upsertContractAllowance(parentId, body),
    del: (parentId: string, allowanceId: string) =>
      api.deleteContractAllowance(parentId, allowanceId),
    queryKey: (parentId: string) => ["contract-allowances", parentId],
    titleKey: "app.aap_title_contract",
    layerLabelKey: "app.aap_layer_contract",
  },
} as const;

function AssignmentRow({
  assignment,
  allowance,
  onEdit,
  onDelete,
}: {
  assignment: AllowanceAssignment;
  allowance: AllowanceDetail | undefined;
  onEdit: (override: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(assignment.amount_override ?? "");

  return (
    <tr>
      <td className="px-3 py-2 font-mono text-xs">
        {allowance?.code ?? assignment.allowance_id.slice(0, 8) + "…"}
      </td>
      <td className="px-3 py-2 text-sm">
        {allowance?.name_en ?? t("app.aap_deleted_allowance")}
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {editing ? (
          <input
            autoFocus
            type="number"
            step="0.01"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onEdit(draft);
                setEditing(false);
              }
              if (e.key === "Escape") setEditing(false);
            }}
            onBlur={() => {
              onEdit(draft);
              setEditing(false);
            }}
            className="w-24 rounded border border-slate-300 px-2 py-0.5"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-start"
          >
            {assignment.amount_override !== null ? (
              <span>{assignment.amount_override}</span>
            ) : (
              <span className="italic text-slate-400">{t("app.aap_catalog_default")}</span>
            )}
          </button>
        )}
      </td>
      <td className="px-3 py-2 text-end">
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-1 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

function AddAllowanceDialog({
  layer,
  parentId,
  legalEntityId,
  alreadyAssignedIds,
  onClose,
  onAdded,
}: {
  layer: Layer;
  parentId: string;
  legalEntityId: string;
  alreadyAssignedIds: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [allowanceId, setAllowanceId] = useState("");
  const [amountOverride, setAmountOverride] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const catalogQ = useQuery({
    queryKey: ["allowances", legalEntityId],
    queryFn: () => api.listAllowances(legalEntityId),
  });

  const mutation = useMutation({
    mutationFn: () =>
      API_BY_LAYER[layer].upsert(parentId, {
        allowance_id: allowanceId,
        amount_override: amountOverride || null,
      }),
    onSuccess: () => {
      onAdded();
      onClose();
    },
    onError: (e: unknown) => {
      setErrorMsg(e instanceof ApiError ? e.detail : String(e));
    },
  });

  const options = (catalogQ.data ?? []).filter(
    (a) => !alreadyAssignedIds.has(a.id),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h2 className="text-lg font-bold">{t("app.aap_add_title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!allowanceId) return;
            mutation.mutate();
          }}
          className="space-y-3 p-4 text-sm"
        >
          <label className="block">
            <span className="font-medium text-slate-700">{t("app.aap_allowance_req")}</span>
            <select
              required
              value={allowanceId}
              onChange={(e) => setAllowanceId(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            >
              <option value="">{t("app.aap_pick_from_catalog")}</option>
              {options.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name_en} ({a.category})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="font-medium text-slate-700">{t("app.aap_amount_override")}</span>
            <input
              type="number"
              step="0.01"
              value={amountOverride}
              onChange={(e) => setAmountOverride(e.target.value)}
              placeholder={t("app.aap_ph_blank_catalog")}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono"
            />
          </label>
          {errorMsg && (
            <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
          )}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
            >
              {t("app.aap_cancel")}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !allowanceId}
              className="rounded bg-slate-900 px-3 py-1.5 font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {mutation.isPending ? t("app.aap_adding") : t("app.aap_add_short")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AllowanceAssignmentsPanel({
  layer,
  parentId,
  legalEntityId,
}: AllowanceAssignmentsPanelProps) {
  const { t } = useTranslation();
  const cfg = API_BY_LAYER[layer];
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const assignmentsQ = useQuery({
    queryKey: cfg.queryKey(parentId),
    queryFn: () => cfg.list(parentId),
  });
  const catalogQ = useQuery({
    queryKey: ["allowances", legalEntityId],
    queryFn: () => api.listAllowances(legalEntityId, { includeInactive: true }),
  });

  const editMutation = useMutation({
    mutationFn: (vars: { allowanceId: string; override: string }) =>
      cfg.upsert(parentId, {
        allowance_id: vars.allowanceId,
        amount_override: vars.override || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cfg.queryKey(parentId) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (allowanceId: string) => cfg.del(parentId, allowanceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: cfg.queryKey(parentId) }),
  });

  const assignments = assignmentsQ.data ?? [];
  const catalog = catalogQ.data ?? [];
  const catalogById = new Map(catalog.map((a) => [a.id, a]));
  const alreadyAssignedIds = new Set(assignments.map((a) => a.allowance_id));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500">
          <Coins className="h-3.5 w-3.5" />
          {t(cfg.titleKey)}
          <span className="ms-1 text-slate-400">({assignments.length})</span>
        </p>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          <Plus className="h-3.5 w-3.5" /> {t("app.aap_add_short")}
        </button>
      </div>

      {assignments.length === 0 ? (
        <p className="text-sm italic text-slate-500">
          {t("app.aap_no_assignments_layer", { layer: t(cfg.layerLabelKey) })}
        </p>
      ) : (
        <table className="w-full border border-slate-100 text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-start">{t("app.aap_col_code")}</th>
              <th className="px-3 py-2 text-start">{t("app.aap_col_name")}</th>
              <th className="px-3 py-2 text-start">{t("app.aap_col_amount_override")}</th>
              <th className="px-3 py-2 text-end">{t("app.aap_col_actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {assignments.map((asg) => (
              <AssignmentRow
                key={asg.id}
                assignment={asg}
                allowance={catalogById.get(asg.allowance_id)}
                onEdit={(override) =>
                  editMutation.mutate({
                    allowanceId: asg.allowance_id,
                    override,
                  })
                }
                onDelete={() => deleteMutation.mutate(asg.allowance_id)}
              />
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddAllowanceDialog
          layer={layer}
          parentId={parentId}
          legalEntityId={legalEntityId}
          alreadyAssignedIds={alreadyAssignedIds}
          onClose={() => setShowAdd(false)}
          onAdded={() => qc.invalidateQueries({ queryKey: cfg.queryKey(parentId) })}
        />
      )}
    </div>
  );
}
